import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle } from '../../icons';
// 채택 3단계 ② 인사 — 단추를 정본 Button 으로(D1).
import { Button } from '../../components/shared';
import {
  fetchRounds, fetchRound, createRound, confirmTargets, confirmGrades, updateEvaluation,
  EVAL_GRADES, EVAL_STAGE_LABEL,
  type RoundDto, type RoundDetailDto, type EvalGrade, type EvalStage, type QuotaBreachDto,
} from '../../api/hr-evaluations';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import './EvaluationsPage.css';

/**
 * 인사평가 — 담당자 화면 (시안 A21, migration 134).
 *
 * ★★ **왼쪽은 회차, 오른쪽은 그 회차의 진행률과 대상자.** 시안이 못박은 것 —
 *    «담당자가 보는 것은 점수가 아니라 누가 아직 안 냈는가» 라, **미제출을 맨 앞에** 세운다.
 * ★★ **자기평가를 직원이 내는 화면은 아직 없다.** 단계는 담당자가 찍는 것이고, 화면이 그
 *    사실을 먼저 말한다 — 누르고 나서 «아무 일도 안 일어났다» 가 되지 않게(인사발령과 같은 규율).
 * ★ **진행률·분포·배분 초과는 전부 서버가 센다.** 화면은 그리기만 한다 — 「미제출」 의 정의가
 *   두 곳에 있으면 지표와 표가 서로 다른 말을 한다.
 * ★ 확정된 회차는 **읽기만** 된다. 단추가 사라지는 것이 아니라 **왜 못 누르는지** 가 보인다.
 */

const STAGE_KEY: Record<EvalStage, 'self' | 'first' | 'second'> = {
  SELF: 'self', FIRST: 'first', SECOND: 'second',
};

const emptyRound = () => ({ name: '', periodStart: '', periodEnd: '' });

export function EvaluationsPage() {
  const [rounds, setRounds] = useState<RoundDto[]>([]);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<RoundDetailDto | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [breaches, setBreaches] = useState<QuotaBreachDto[]>([]);
  const [form, setForm] = useState(emptyRound);
  const [adding, setAdding] = useState(false);

  const loadRounds = useCallback(async () => {
    const res = await fetchRounds();
    setRounds(res.rounds);
    setPickedId((cur) => cur ?? res.rounds[0]?.id ?? null);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await loadRounds();
        if (alive) setState('ok');
      } catch {
        if (alive) setState('folded');
      }
    })();
    return () => { alive = false; };
  }, [loadRounds]);

  const loadDetail = useCallback(async (id: string) => {
    try {
      const d = await fetchRound(id);
      setDetail(d);
      setBreaches(d.breaches);
    } catch {
      setDetail(null);
    }
  }, []);

  useEffect(() => {
    if (!pickedId) { setDetail(null); return; }
    void loadDetail(pickedId);
  }, [pickedId, loadDetail]);

  const round = detail?.round ?? null;
  const locked = Boolean(round?.confirmedAt);

  const quotaText = useMemo(() => {
    if (!round) return '';
    const q = Object.entries(round.quota).filter(([, v]) => v > 0);
    return q.length > 0 ? q.map(([g, v]) => `${g} ${v}%`).join(' · ') : '제한 없음';
  }, [round]);

  const refresh = async () => {
    if (pickedId) await loadDetail(pickedId);
    await loadRounds();
  };

  const addRound = async () => {
    if (!form.name.trim() || !form.periodStart || !form.periodEnd) {
      setError('이름과 기간은 있어야 합니다');
      return;
    }
    setBusy('new');
    setError(null);
    try {
      const created = await createRound({ ...form, name: form.name.trim() });
      setForm(emptyRound());
      setAdding(false);
      await loadRounds();
      setPickedId(created.id);
    } catch (e) {
      setError(msgOf(e) ?? '회차를 만들지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const runTargets = async () => {
    if (!pickedId) return;
    setBusy('targets');
    setError(null);
    setNotice(null);
    try {
      const r = await confirmTargets(pickedId);
      // ★ 삼항을 괄호로 묶는다 — 안 묶으면 `+` 가 **else 가지에만** 붙어, 대상이 더해진
      //   경우에 「입사일이 없어 빠졌다」 가 통째로 사라진다(테스트가 이것을 잡았다).
      const head = r.added > 0
        ? `${r.added}명을 대상에 더했습니다 (전체 ${r.total}명)`
        : `새로 대상이 된 사람이 없습니다 (전체 ${r.total}명)`;
      const tail = r.skippedNoHireDate > 0
        ? ` · 입사일이 없어 ${r.skippedNoHireDate}명은 빠졌습니다`
        : '';
      setNotice(head + tail);
      await refresh();
    } catch (e) {
      setError(msgOf(e) ?? '대상을 확정하지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const runConfirm = async () => {
    if (!pickedId) return;
    setBusy('confirm');
    setError(null);
    setNotice(null);
    try {
      await confirmGrades(pickedId);
      setBreaches([]);
      setNotice('등급을 확정했습니다 — 이 회차는 이제 읽기만 됩니다');
      await refresh();
    } catch (e) {
      const data = (e as { response?: { data?: { message?: string; breaches?: QuotaBreachDto[] } } }).response?.data;
      if (data?.breaches) setBreaches(data.breaches);
      setError(data?.message ?? '등급을 확정하지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const mark = async (rowId: string, stage: EvalStage, on: boolean) => {
    setBusy(rowId);
    setError(null);
    try {
      await updateEvaluation(rowId, { [STAGE_KEY[stage]]: on });
      await refresh();
    } catch (e) {
      setError(msgOf(e) ?? '기록하지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const setGrade = async (rowId: string, grade: string) => {
    setBusy(rowId);
    setError(null);
    try {
      await updateEvaluation(rowId, { grade: (grade || null) as EvalGrade | null });
      await refresh();
    } catch (e) {
      setError(msgOf(e) ?? '등급을 매기지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="hrpage hrev">
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/evaluations')}
        scope={scopeFor('/erp/hr/evaluations')}
        title="인사평가"
        description="회차를 세우고, 누가 아직 안 냈는지를 봅니다"
      />

      {state === 'folded' && (
        <div className="hrpage__note">인사평가를 불러오지 못했습니다 — 서버가 아직 준비되지 않았을 수 있습니다.</div>
      )}

      {state !== 'folded' && (
        <>
          <div className="hrpage__note">
            자기평가를 직원이 직접 내는 화면은 아직 없습니다 — 지금은 <b>담당자가 받은 것을 여기에 표시</b>합니다.
          </div>
          {error && <p className="hrcert__err" role="alert">{error}</p>}
          {notice && <p className="hrev__notice" role="status">{notice}</p>}

          <div className="hrev__split">
            {/* ── 왼쪽: 회차 ───────────────────────── */}
            <section className="hrpage__panel hrev__side">
              <h2 className="hrpage__panelt">
                회차
                <span className="hrpage__panels">{rounds.length}개</span>
              </h2>
              {rounds.length === 0 ? (
                <p className="hrpage__empty">아직 만든 회차가 없습니다.</p>
              ) : (
                <ul className="hrev__rounds">
                  {rounds.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        className={`hrev__round${r.id === pickedId ? ' is-picked' : ''}`}
                        onClick={() => { setPickedId(r.id); setNotice(null); setError(null); }}
                        aria-current={r.id === pickedId}
                      >
                        <span className="hrev__roundnm">{r.name}</span>
                        <span className={`hrpage__pill hrev__st is-${r.status.toLowerCase()}`}>{r.statusLabel}</span>
                        <span className="hrev__roundsub">
                          {r.periodStart} ~ {r.periodEnd}
                          {typeof r.targetCount === 'number' && ` · 대상 ${r.targetCount}명`}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {adding ? (
                <div className="hrcert__form hrev__newform">
                  <label className="hrcert__field">
                    <span>이름</span>
                    <input
                      value={form.name} maxLength={60} placeholder="예) 2026 상반기"
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </label>
                  <label className="hrcert__field">
                    <span>시작</span>
                    <input type="date" value={form.periodStart} onChange={(e) => setForm({ ...form, periodStart: e.target.value })} />
                  </label>
                  <label className="hrcert__field">
                    <span>종료</span>
                    <input type="date" value={form.periodEnd} onChange={(e) => setForm({ ...form, periodEnd: e.target.value })} />
                  </label>
                  <Button variant="primary" onClick={addRound} disabled={busy === 'new'}>
                    {busy === 'new' ? '만드는 중…' : '만들기'}
                  </Button>
                </div>
              ) : (
                <button type="button" className="hrev__add" onClick={() => setAdding(true)}>회차 만들기</button>
              )}
            </section>

            {/* ── 오른쪽: 진행률 + 대상자 ─────────────── */}
            <div className="hrev__main">
              {!round ? (
                <section className="hrpage__panel">
                  <p className="hrpage__empty">왼쪽에서 회차를 고르세요.</p>
                </section>
              ) : (
                <>
                  <section className="hrpage__panel">
                    <h2 className="hrpage__panelt">
                      {round.name}
                      <span className="hrpage__panels">
                        {locked ? '등급 확정됨 — 읽기만 됩니다' : round.statusLabel}
                      </span>
                    </h2>
                    <table className="hrpage__tbl">
                      <tbody>
                        <tr><td>기간</td><td className="hrpage__dim">{round.periodStart} ~ {round.periodEnd}</td></tr>
                        <tr>
                          <td>대상</td>
                          <td className="hrpage__dim">
                            {round.eligibleMonths > 0 ? `재직 ${round.eligibleMonths}개월 이상` : '재직 전원'}
                            {detail && ` · ${detail.progress.target}명`}
                          </td>
                        </tr>
                        <tr>
                          <td>단계</td>
                          <td className="hrpage__dim">
                            {round.stages.length > 0
                              ? round.stages.map((s) => EVAL_STAGE_LABEL[s]).join(' → ')
                              : '쓰는 단계가 없습니다'}
                          </td>
                        </tr>
                        <tr><td>등급 배분</td><td className="hrpage__dim">{quotaText}</td></tr>
                      </tbody>
                    </table>
                  </section>

                  {detail && (
                    <div className="hrpage__kpis">
                      <div className="hrpage__kpi">
                        <span className="hrpage__kpil">대상</span>
                        <span className="hrpage__kpiv">{detail.progress.target}<small>명</small></span>
                        <span className="hrpage__kpid">
                          {round.eligibleMonths > 0 ? `재직 ${round.eligibleMonths}개월 이상` : '재직 전원'}
                        </span>
                      </div>
                      {detail.progress.stages.map((s) => (
                        <div className="hrpage__kpi" key={s.stage}>
                          <span className="hrpage__kpil">{s.label}</span>
                          <span className="hrpage__kpiv">{s.done}<small>명</small></span>
                          <span className="hrpage__kpid">제출률 {s.rate}%</span>
                        </div>
                      ))}
                      <div className="hrpage__kpi hrev__kpi--pending">
                        <span className="hrpage__kpil">미제출</span>
                        <span className="hrpage__kpiv">{detail.progress.pending}<small>명</small></span>
                        <span className="hrpage__kpid">독촉 대상 · 등급 {detail.progress.graded}명</span>
                      </div>
                    </div>
                  )}

                  {breaches.length > 0 && (
                    <div className="hrasg__overdue" role="status">
                      <AlertCircle size={16} aria-hidden />
                      등급 배분을 넘었습니다 —{' '}
                      {breaches.map((b) => `${b.grade} 가 ${b.over}명 초과(${b.actual}/${b.allowed})`).join(' · ')}.
                      확정 단계에서 막힙니다.
                    </div>
                  )}

                  <section className="hrpage__panel">
                    <h2 className="hrpage__panelt">
                      대상자
                      <span className="hrpage__panels">{detail?.rows.length ?? 0}명</span>
                      <span className="hrev__acts">
                        <Button variant="secondary" size="compact" onClick={runTargets} disabled={locked || busy === 'targets'}>
                          {busy === 'targets' ? '확정 중…' : '대상 확정'}
                        </Button>
                        <Button variant="secondary" size="compact" onClick={runConfirm} disabled={locked || busy === 'confirm'}>
                          {busy === 'confirm' ? '확정 중…' : '등급 확정'}
                        </Button>
                      </span>
                    </h2>

                    {!detail || detail.rows.length === 0 ? (
                      <p className="hrpage__empty">
                        아직 대상자가 없습니다 — «대상 확정» 을 누르면 자격이 되는 사람이 명단에 섭니다.
                      </p>
                    ) : (
                      <table className="hrpage__tbl hrev__tbl">
                        <thead>
                          <tr>
                            <th>피평가자</th>
                            <th>소속</th>
                            {round.stages.map((s) => <th key={s}>{EVAL_STAGE_LABEL[s]}</th>)}
                            <th className="hrpage__num">점수</th>
                            <th>등급</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.rows.map((r) => (
                            <tr key={r.id} className={r.complete ? '' : 'is-overdue'}>
                              <td>
                                {r.name ?? '—'}
                                {!r.complete && r.nextStage && (
                                  <span className="hrpage__pill hrev__due">
                                    {EVAL_STAGE_LABEL[r.nextStage]} 대기
                                  </span>
                                )}
                              </td>
                              <td className="hrpage__dim">{r.teamName ?? '—'}</td>
                              {round.stages.map((s) => {
                                const on = r[STAGE_KEY[s]];
                                return (
                                  <td key={s}>
                                    <button
                                      type="button"
                                      className={`hrev__mark${on ? ' is-on' : ''}`}
                                      onClick={() => mark(r.id, s, !on)}
                                      disabled={locked || busy === r.id}
                                      aria-pressed={on}
                                      aria-label={`${r.name ?? ''} ${EVAL_STAGE_LABEL[s]} ${on ? '제출' : '대기'}`}
                                    >
                                      {on ? '제출' : '대기'}
                                    </button>
                                  </td>
                                );
                              })}
                              <td className="hrpage__num">{r.score ?? '—'}</td>
                              <td>
                                <select
                                  className="hrev__grade" value={r.grade ?? ''}
                                  disabled={locked || busy === r.id}
                                  aria-label={`${r.name ?? ''} 등급`}
                                  onChange={(e) => setGrade(r.id, e.target.value)}
                                >
                                  <option value="">—</option>
                                  {EVAL_GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </section>

                  {detail && (
                    <section className="hrpage__panel">
                      <h2 className="hrpage__panelt">
                        등급 분포
                        <span className="hrpage__panels">매겨진 {detail.progress.graded}명 · 상한은 대상자 전체 기준</span>
                      </h2>
                      <table className="hrpage__tbl">
                        <thead>
                          <tr><th>등급</th><th>배분</th><th className="hrpage__num">허용</th><th className="hrpage__num">지금</th></tr>
                        </thead>
                        <tbody>
                          {EVAL_GRADES.map((g) => {
                            const pct = round.quota[g] ?? 0;
                            const allowed = pct > 0 ? Math.max(1, Math.floor((detail.progress.target * pct) / 100)) : null;
                            const actual = detail.distribution[g] ?? 0;
                            const over = allowed !== null && actual > allowed;
                            return (
                              <tr key={g}>
                                <td>{g}</td>
                                <td className="hrpage__dim">{pct > 0 ? `${pct}%` : '제한 없음'}</td>
                                <td className="hrpage__num hrpage__dim">{allowed ?? '—'}</td>
                                <td className={`hrpage__num${over ? ' hrev__over' : ''}`}>{actual}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </section>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function msgOf(e: unknown): string | null {
  return (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? null;
}
